import api from './api';

export async function placeOrder({ type, productId, quantity, note, expectedUnitPrice }) {
  const res = await api.post('/orders', { type, productId, quantity, note, expectedUnitPrice });
  return res.data;
}

// Admin: Place order for a specific user
export async function placeOrderForUser({ userId, type, productId, quantity, note, expectedUnitPrice }) {
  const res = await api.post('/orders/for-user', { userId, type, productId, quantity, note, expectedUnitPrice });
  return res.data;
}

export async function getMyOrders({ type, search } = {}) {
  const params = {};
  if (type) params.type = type;
  if (search) params.search = search;

  const res = await api.get('/orders/me', { params });
  return res.data;
}

export async function getAllOrders({ type, search, userId, page = 1, limit = 50 } = {}) {
  const params = { page, limit };
  if (type) params.type = type;
  if (search) params.search = search;
  if (userId) params.userId = userId;

  const res = await api.get('/orders', { params });
  // Returns { data, total, hasMore, page, limit }
  return res.data;
}

export async function approveOrder(id, note) {
  await api.put(`/orders/${id}/approve`, { note });
}

export async function rejectOrder(id, note) {
  await api.put(`/orders/${id}/reject`, { note });
}

// Admin: Update order (full editing capability)
export async function updateOrder(id, updates) {
  const res = await api.patch(`/orders/${id}`, updates);
  return res.data;
}

// Admin: Delete order (soft delete)
export async function deleteOrder(id) {
  await api.delete(`/orders/${id}`);
}

// Admin: Get order edit history
export async function getOrderHistory(id) {
  const res = await api.get(`/orders/${id}/history`);
  return res.data;
}

// Admin: Get pending orders count (for badge)
export async function fetchPendingOrdersCount() {
  const res = await api.get('/orders/pending/count');
  return res.data;
}

/**
 * Add a trader order
 * @param {Object} orderData - Order data containing tradingProductId, type, price, weight, count, description
 * @returns {Promise<Object>} Created order data
 */
export async function addTraderOrder(orderData) {
  const { data } = await api.post('/trading/order/traderorderadd', orderData);

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "ثبت معامله ناموفق بود";
    throw new Error(errorMessage);
  }

  // Return data.data if it exists, otherwise return null (API might return empty data.data)
  return data || null;
}

/**
 * Add a trader currency order
 * @param {Object} orderData - Order data containing currencyId, type, price, currencyValue, totalPrice, description
 * @returns {Promise<Object>} Created order data
 */
export async function addTraderCurrencyOrder(orderData) {
  const { data } = await api.post('/trading/order/traderordercurrencyadd', orderData);

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "ثبت معامله ارز ناموفق بود";
    throw new Error(errorMessage);
  }

  return data || null;
}

/**
 * Cancel a trader order
 * @param {number} orderId - The ID of the order to cancel
 * @returns {Promise<Object>} Cancellation result
 */
export async function cancelTraderOrder(orderId) {
  const { data } = await api.post('/trading/order/traderordercancelled', {
    id: orderId,
    isCancelled: true
  });

  if (!data.isSuccessful) {
    const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "لغو سفارش ناموفق بود";
    throw new Error(errorMessage);
  }

  return data.data || null;
}

/**
 * List trader orders
 * @param {Object} params - Optional query parameters including PageNumber and PageSize
 * @returns {Promise<Object>} Object containing orders array and pagination info
 */
export async function listTraderOrders(params = {}) {
  // Set default pagination if not provided
  const requestParams = {
    PageNumber: params.PageNumber || params.pageNumber || 1,
    PageSize: params.PageSize || params.pageSize || 10,
    ...params
  };

  const { data } = await api.get('/trading/order/traderorderlist', { params: requestParams });

  // Handle API response structure with isSuccessful wrapper
  // Structure: { isSuccessful: true, data: { data: [...], currentPage, totalPages, ... } }
  if (data.isSuccessful !== undefined) {
    if (!data.isSuccessful) {
      const errorMessage = data.getMessageText?.join(", ") || data.message?.[0]?.messageText || "دریافت لیست معاملات ناموفق بود";
      throw new Error(errorMessage);
    }

    // Check if data.data exists and has pagination structure
    if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
      // New structure: data.data is an object with { data: [...], currentPage, totalPages, ... }
      const paginatedData = data.data;
      const orders = Array.isArray(paginatedData.data) ? paginatedData.data : [];

      return {
        orders: orders,
        totalCount: paginatedData.totalCount || 0,
        currentPage: paginatedData.currentPage || requestParams.PageNumber,
        totalPages: paginatedData.totalPages || 1,
        pageSize: paginatedData.pageSize || requestParams.PageSize,
        hasMore: paginatedData.hasNextPage || false,
        hasPreviousPage: paginatedData.hasPreviousPage || false
      };
    }

    // Handle case where data.data is directly an array (old structure)
    const orders = Array.isArray(data.data) ? data.data : [];

    // If API returns pagination metadata at top level, include it
    if (data.totalCount !== undefined || data.hasMore !== undefined) {
      return {
        orders: orders,
        totalCount: data.totalCount,
        pageNumber: data.pageNumber || requestParams.PageNumber,
        pageSize: data.pageSize || requestParams.PageSize,
        hasMore: data.hasMore !== undefined ? data.hasMore : (orders.length >= requestParams.PageSize)
      };
    }

    // Backward compatibility: if API returns just array, wrap it
    return {
      orders: orders,
      totalCount: orders.length,
      pageNumber: requestParams.PageNumber,
      pageSize: requestParams.PageSize,
      hasMore: orders.length >= requestParams.PageSize
    };
  }

  // Handle direct response structure without isSuccessful wrapper
  // Structure: { data: [...], currentPage, totalPages, ... }
  if (data.data && Array.isArray(data.data) && data.hasNextPage !== undefined) {
    return {
      orders: data.data,
      totalCount: data.totalCount || 0,
      currentPage: data.currentPage || requestParams.PageNumber,
      totalPages: data.totalPages || 1,
      pageSize: data.pageSize || requestParams.PageSize,
      hasMore: data.hasNextPage || false,
      hasPreviousPage: data.hasPreviousPage || false
    };
  }

  // If API returns array directly (backward compatibility)
  const orders = Array.isArray(data) ? data : [];
  return {
    orders: orders,
    totalCount: orders.length,
    pageNumber: requestParams.PageNumber,
    pageSize: requestParams.PageSize,
    hasMore: orders.length >= requestParams.PageSize
  };
}


